import { useTranslation } from "react-i18next";
import { FaUser } from "react-icons/fa";
import { LuUser } from "react-icons/lu";
import { cn } from "@/lib/utils";

interface HomeConfirmationProgressProps {
  goingCount: number;
  minPlayers: number;
  maxPlayers: number;
  confirmed: boolean;
  minConfirmPct: number;
}

export function HomeConfirmationProgress({
  goingCount,
  minPlayers,
  maxPlayers,
  confirmed,
  minConfirmPct,
}: HomeConfirmationProgressProps) {
  const { t } = useTranslation();

  const slots = Math.max(maxPlayers, 1);
  const filled = Math.min(Math.max(goingCount, 0), slots);

  return (
    <div className="space-y-2.5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("v2.home.confirmProgressTitle")}
        </p>
        <p className="text-lg font-black tabular-nums leading-tight">
          {t("v2.home.confirmProgressCount", { going: goingCount, max: slots })}
        </p>
      </div>

      <div
        className="flex flex-wrap gap-1.5"
        role="img"
        aria-label={t("v2.home.confirmProgressAria", {
          going: goingCount,
          max: slots,
          min: minPlayers,
        })}
      >
        {Array.from({ length: slots }, (_, index) =>
          index < filled ? (
            <FaUser
              key={index}
              className={cn(
                "h-5 w-5 shrink-0 transition-colors duration-300",
                confirmed
                  ? "text-soccer-primary"
                  : "text-amber-600 dark:text-amber-400"
              )}
              aria-hidden
            />
          ) : (
            <LuUser
              key={index}
              className="h-5 w-5 shrink-0 text-muted-foreground/35 transition-colors duration-300"
              aria-hidden
            />
          )
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {confirmed
          ? t("v2.home.confirmedHeadcount", { going: goingCount })
          : t("v2.home.confirmProgressHint", { pct: minConfirmPct, min: minPlayers })}
        {goingCount > slots
          ? ` · ${t("v2.home.confirmProgressReserveOverflow", { count: goingCount - slots })}`
          : null}
      </p>
    </div>
  );
}
